<template lang="pug">
div
    div
        Info(:data="pData")
        div(v-if="pData.hasOverriddenFeatureBullets" v-html="pData.features.content" class="content")
        div(v-if="pData.orderBullets" class="content")
            ul(class="bullet-list list-inside")
                li(v-for="bullet in pData.orderBullets" v-html="bullet")
        div(v-if="pData.productId && pData.installation && pData.installation.files && pData.installation.files.length == 1 && pData.hasOverriddenInstallGuide" class="content")
            p #[a(:href="pData.installation.files[0].url" target="_blank") Download Installation Instructions]

        ProductMessage(v-if="pData.isPreOrder")
            template(#header) Product Available for Pre-order
            template(#text) Purchase today and the product will be shipped to you once it becomes available. #[b(v-if="pData.preOrderDate") Estimated ship date: {{formatDate}}]
            template(#disclaimer) Payment will be processed in full at time of purchase

        div
            Drill(
                v-for="drill in pControls.drills"
                v-if="!drill.isFitment"
                :id="`scrollTarget-${drill.type}`"
                :key="drill.type"
                :drill="drill"
                :currentValue="drill.currentValue"
                data-testid="productInfo"
                @changed="drillChanged"
                @changedNative="resetControls"
                @click.native="alert(drill.isDisabled)"
            )

            div(v-if="pControls.drillConfig.splitAt || pControls.integration")
                BaseButton(
                    @click.native="chooseOptionsClicked"
                    size="MEDIUM"
                    :isDisabled="isChooseOptionsDisabled"
                    class="w-full"
                )
                    template(v-if="!isProductPage") Choose Options
                    template(v-else) Add to Cart

                DealerLocatorLink(
                    mode="link"
                    :data="pData"
                )

            template(v-else)
                SkuWarning(v-if="mismatchedProduct && !hasInteraction")

                ProductMessage(v-if="pControls.hasOrderControlError" :showErrorIcon="true")

                template(v-else-if="pData.isDealerOnly")
                    DealerLocatorLink(:data="pData")

                template(v-else)
                    div(class="space-y-2")
                        AddToCart(
                            :disabled="isAddToCartDisabled"
                            :isOutOfStock="pData.isOutOfStock"
                            :sku="pData.sku"
                            :qtySelector="pData.qtySelector"
                            :addFitment="!isProductPage"
                            ref="primaryAddToCart"
                            :isPreOrder="pData.isPreOrder"
                            @click.native="addToCartClicked"
                        )

                        BackInStockNotification(
                            v-if="$sitewideData.config.backInStockNotificationEnabled && $cognito.enabled && pData.isOutOfStock && !pData.isDisabled"
                            :productLineSlug="pData.productLineSlug"
                            :skuSlug="pData.skuSlug"
                        )

                    //- I'll need to render this section only if split at is false
                    div(
                        v-if="!pControls.drillConfig.splitAt && showFinancing && (pData.actualPrice && pData.actualPrice.min)"
                        class="pt-2"
                    )
                        AffirmAsLowAs(
                            v-if="$affirm.enabled"
                            :price="pData.actualPrice.min"
                            pageType="product"
                            alignment="left"
                        )
                        PaypalMessaging(
                            v-if="$paypal.enabled"
                            :price="pData.actualPrice.min"
                            pageType="product"
                        )

                    DealerLocatorLink(
                        mode="link"
                        :data="pData"
                    )

</template>

<script>
import Card from '@/components/style/card'
import BaseButton from '@/components/baseButton'
import { linkBuilders } from '@/helpers/mixins'
import Phone from '@/assets/common/phoneOutline?inline'
import Drill from './drill'
import Info from './info'
import AddToCart from './addToCart'
import DealerLocatorLink from './dealerLocatorLink'
const AffirmAsLowAs = () =>
    import(/* webpackChunkName: "affirm-as-low-as" */ '@/integrations/affirm/affirmAsLowAs')
const PaypalMessaging = () =>
    import(/* webpackChunkName: "paypal-messaging" */ '@/integrations/paypal/messaging')
const ProductMessage = () => import(/* webpackChunkName: "product-message" */ './productMessage')
const SkuWarning = () => import(/* webpackChunkName: "sku-warning" */ './skuWarning')
const BackInStockNotification = () =>
    import(/* webpackChunkName: "back-in-stock-notification" */ './backInStockNotification')

export default {
    components: {
        Card,
        Drill,
        Info,
        AddToCart,
        DealerLocatorLink,
        BackInStockNotification,
        AffirmAsLowAs,
        BaseButton,
        PaypalMessaging,
        ProductMessage,
        SkuWarning,
        Phone,
    },
    mixins: [linkBuilders],
    inject: ['alert', '$pageBus'],
    data() {
        return {
            showFinancing: true,
        }
    },
    computed: {
        isChooseOptionsDisabled() {
            return this.pControls.groups.length === 0 && !this.pControls.integration
        },
        isAddToCartDisabled() {
            return (
                !this.pData.skuSlug ||
                this.pData.isOutOfStock ||
                (!this.pControls.isGoodVehicleSoFar && this.isProductPage)
            )
        },
        mismatchedProduct() {
            return this.$store.state.product.mismatchedProduct
        },
        hasInteraction() {
            return this.$store.state.product.controls.hasInteraction
        },
        pData() {
            return this.$store.state.product.data
        },
        pControls() {
            return this.$store.state.product.controls
        },
        isProductPage() {
            return this.$route.meta.isProductPage
        },
        isFeederPage() {
            return this.$store.state.product.fromAd
        },
        formatDate() {
            return new Date(this.pData.preOrderDate).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: '2-digit',
            })
        },
    },
    created() {
        if (this.pData.name.toLowerCase().includes('gift card')) this.showFinancing = false
    },
    mounted() {
        if (this.$invoca.enabled) this.$invoca.changePhone()
    },
    methods: {
        // not for YMMBBE
        drillChanged(obj) {
            this.$store.dispatch('product/fillNextDrill', obj)
        },
        resetControls(obj) {
            this.$store.dispatch('product/resetControls', obj) // reset controls 2 below this one
        },
        addToCartClicked() {
            if (this.mismatchedProduct && !this.hasInteraction) {
                this.$pageBus.$emit('blinkMismatched')
                return
            }

            if (!this.pControls.isGoodVehicleSoFar && this.isProductPage)
                this.$pageBus.$emit('changeVehicle')

            this.alert(this.isAddToCartDisabled)
        },
        chooseOptionsClicked() {
            if (!this.isChooseOptionsDisabled) return this.$uiEvents.$emit('chooseOptionsClicked')
            if (!this.pControls.isGoodVehicleSoFar) this.$pageBus.$emit('changeVehicle')
            this.alert(true)
        },
    },
}
</script>

<style lang="stylus" scoped>
// Override generic.styl
.content
    .bullet-list
        margin 0
        margin-top 1rem
        margin-bottom 1rem
</style>
